/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode, useCallback } from 'react';
import { safeLocalStorageGetJson, safeLocalStorageSetJson, warnOnQuotaOnce } from '@/lib/safe-storage';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetchGet, apiFetch } from '@/lib/api-fetch';

export interface Settings {
  mode: 'simple' | 'pro';
  theme: 'dark' | 'light' | 'auto';
  locale: 'fr' | 'en';
  aiPreference: 'auto' | 'openrouter' | 'minimax';
  /** Tone for AI CV optimization suggestions */
  aiTone: 'formel' | 'dynamique' | 'neutre';
  /** Length of AI CV optimization output */
  aiLength: 'court' | 'standard' | 'detaille';
  defaultExportFormat: 'pdf' | 'docx' | 'typst';
  defaultLayout: 'classique' | 'moderne' | 'ats' | 'pro';
  analyticsOptIn: boolean;
  /** Cover letter settings — persisted so last choice is remembered */
  coverLetterLength: 'flash' | 'short' | 'standard' | 'long';
  coverLetterTone: 'direct' | 'professionnel' | 'dynamique' | 'enthousiaste';
  coverLetterFidelity: 'fidele' | 'modere' | 'creatif';
  /** Default ATS preset applied when no per-template config is saved */
  defaultAtsPreset: 'conservateur' | 'standard' | 'inventif';
}

const DEFAULT_SETTINGS: Settings = {
  mode: 'pro',
  theme: 'dark',
  locale: 'fr',
  aiPreference: 'auto',
  aiTone: 'neutre',
  aiLength: 'standard',
  defaultExportFormat: 'pdf',
  defaultLayout: 'classique',
  analyticsOptIn: true,
  coverLetterLength: 'standard',
  coverLetterTone: 'professionnel',
  coverLetterFidelity: 'modere',
  defaultAtsPreset: 'standard',
};

const STORAGE_KEY = 'atsmycv_settings_v1';

interface SettingsContextValue {
  settings: Settings;
  update: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  updateMany: (partial: Partial<Settings>) => void;
  reset: () => void;
  isOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    const stored = safeLocalStorageGetJson<Partial<Settings>>(STORAGE_KEY);
    if (stored) return { ...DEFAULT_SETTINGS, ...stored };

    // Migration: read legacy keys from previous hooks
    const legacyTheme = (typeof localStorage !== 'undefined' ? localStorage.getItem('cv_theme') : null) as 'dark' | 'light' | null;
    const legacyLocale = (typeof localStorage !== 'undefined' ? localStorage.getItem('cv_locale') : null) as 'fr' | 'en' | null;
    const legacyMode = (typeof localStorage !== 'undefined' ? localStorage.getItem('cv_app_mode') : null) as 'simple' | 'pro' | null;
    return {
      ...DEFAULT_SETTINGS,
      theme: legacyTheme || DEFAULT_SETTINGS.theme,
      locale: legacyLocale || DEFAULT_SETTINGS.locale,
      mode: legacyMode || DEFAULT_SETTINGS.mode,
    };
  });
  const [isOpen, setIsOpen] = useState(false);
  const { session } = useAuth();
  const savePrefsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);

  // Persist on change
  useEffect(() => {
    warnOnQuotaOnce(safeLocalStorageSetJson(STORAGE_KEY, settings));
  }, [settings]);

  // Au login : charger les préférences depuis le serveur (le serveur a autorité)
  useEffect(() => {
    if (!session?.access_token) return;
    apiFetchGet<{ preferences: Partial<Settings> }>('/api/user/preferences')
      .then(({ json }) => {
        if (json.preferences && Object.keys(json.preferences).length > 0) {
          setSettings((prev) => ({ ...prev, ...json.preferences }));
        }
      })
      .catch(() => { /* offline : conserver le localStorage */ });
  }, [session?.access_token]);

  // Sur changement de settings : push debounced vers le serveur (5s)
  // On skip le premier render (chargement initial depuis localStorage)
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    if (!session?.access_token) return;
    if (savePrefsDebounceRef.current) clearTimeout(savePrefsDebounceRef.current);
    savePrefsDebounceRef.current = setTimeout(() => {
      apiFetch('/api/user/preferences', { preferences: settings }, { method: 'PUT' }).catch(() => { /* ignore */ });
    }, 5000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // Sync theme to document and legacy localStorage key
  useEffect(() => {
    const resolvedTheme = settings.theme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : settings.theme;
    document.documentElement.dataset.theme = resolvedTheme;
    try { localStorage.setItem('cv_theme', resolvedTheme); } catch { /* ignore */ }
  }, [settings.theme]);

  // Sync locale to document and legacy localStorage key
  useEffect(() => {
    document.documentElement.lang = settings.locale;
    try { localStorage.setItem('cv_locale', settings.locale); } catch { /* ignore */ }
  }, [settings.locale]);

  // Sync mode to legacy localStorage key
  useEffect(() => {
    try { localStorage.setItem('cv_app_mode', settings.mode); } catch { /* ignore */ }
  }, [settings.mode]);

  const update = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateMany = useCallback((partial: Partial<Settings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULT_SETTINGS), []);
  const openSettings = useCallback(() => setIsOpen(true), []);
  const closeSettings = useCallback(() => setIsOpen(false), []);

  return (
    <SettingsContext.Provider value={{ settings, update, updateMany, reset, isOpen, openSettings, closeSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
